import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|History|Exam Vouchers|Details|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ExamCertMetricsVouchers,
	portal: 'SP',
};

type LocNameStart = 'SPCandidateHistoryExamVouchersDetails';
export type SPCandidateHistoryExamVouchersDetailsLocNames =
	| `${LocNameStart}Title`
	| `${LocNameStart}NotificationChangesSaved`
	| `${LocNameStart}ButtonEdit`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonUpdate`
	| `${LocNameStart}LabelExamCode`
	| `${LocNameStart}LabelExamName`
	| `${LocNameStart}LabelVoucherRecordId`
	| `${LocNameStart}LabelDiscountCode`
	| `${LocNameStart}LabelUpdated`
	| `${LocNameStart}LabelActiveDate`
	| `${LocNameStart}LabelInactiveDate`
	| `${LocNameStart}LabelInvalid`
	| `${LocNameStart}LabelMaxAttempts`
	| `${LocNameStart}LabelMaxRetakes`
	| `${LocNameStart}LabelNetAcademyId`
	| `${LocNameStart}ErrorMaxAttemptsNumeric`;

export const spCandidateHistoryExamVouchersDetailsLocs: LocDefs<SPCandidateHistoryExamVouchersDetailsLocNames> =
	{
		SPCandidateHistoryExamVouchersDetailsTitle: {
			defaultValue: 'Exam Vouchers/Discounts Details',
			description: 'Title',
			displayPath: `${pathStart}Title`,
			displayType: 'Title',
			legacyName: 'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.Title.Tab',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsNotificationChangesSaved: {
			defaultValue: 'Changes saved',
			description: 'Changes Saved',
			displayPath: `${pathStart}Changes Saved`,
			displayType: 'Notification',
			legacyName:
				'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.NotificationMessage.ChangesSaved',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsButtonEdit: {
			defaultValue: 'Edit',
			description: 'Edit button',
			displayPath: `${pathStart}Edit button`,
			displayType: 'Button',
			legacyName: 'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.Button.Edit',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsButtonCancel: {
			defaultValue: 'Cancel',
			description: 'Cancel button',
			displayPath: `${pathStart}Cancel button`,
			displayType: 'Button',
			legacyName: 'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.Button.Cancel',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsButtonUpdate: {
			defaultValue: 'Update Exam Voucher/Discount',
			description: 'Update button',
			displayPath: `${pathStart}Update button`,
			displayType: 'Button',
			legacyName: 'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.Button.Submit',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsLabelExamCode: {
			defaultValue: 'Exam Code',
			description: 'Column heading for: Exam Code.',
			displayPath: `${pathStart}Exam Code`,
			displayType: 'Label',
			legacyName:
				'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.FieldLabel.ExamCode',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsLabelExamName: {
			defaultValue: 'Exam Name',
			description: 'Column heading for: Exam Name.',
			displayPath: `${pathStart}Exam Name`,
			displayType: 'Label',
			legacyName:
				'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.FieldLabel.ExamName',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsLabelVoucherRecordId: {
			defaultValue: 'Voucher Record ID',
			description: 'Column heading for: Voucher Record ID.',
			displayPath: `${pathStart}Voucher Record ID`,
			displayType: 'Label',
			legacyName:
				'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.FieldLabel.VoucherRecordId',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsLabelDiscountCode: {
			defaultValue: 'Discount Code',
			description: 'Column heading for: Discount Code.',
			displayPath: `${pathStart}Discount Code`,
			displayType: 'Label',
			legacyName:
				'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.FieldLabel.DiscountCode',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsLabelUpdated: {
			defaultValue: 'Updated',
			description: 'Column heading for: Updated.',
			displayPath: `${pathStart}Updated`,
			displayType: 'Label',
			legacyName: 'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.FieldLabel.Updated',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsLabelActiveDate: {
			defaultValue: 'Active Date',
			description: 'Column heading for: Active Date.',
			displayPath: `${pathStart}Active Date`,
			displayType: 'Label',
			legacyName:
				'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.FieldLabel.ActiveDate',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsLabelInactiveDate: {
			defaultValue: 'Inactive Date',
			description: 'Column heading for: Inactive Date.',
			displayPath: `${pathStart}Inactive Date`,
			displayType: 'Label',
			legacyName:
				'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.FieldLabel.InactiveDate',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsLabelInvalid: {
			defaultValue: 'Invalid',
			description: 'Column heading for: Invalid.',
			displayPath: `${pathStart}Invalid`,
			displayType: 'Label',
			legacyName: 'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.FieldLabel.Invalid',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsLabelMaxAttempts: {
			defaultValue: 'Max Attempts',
			description: 'Column heading for: Max Attempts.',
			displayPath: `${pathStart}Max Attempts`,
			displayType: 'Label',
			legacyName:
				'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.FieldLabel.MaxAttempts',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsLabelMaxRetakes: {
			defaultValue: 'Max Retakes',
			description: 'Column heading for: Max Retakes.',
			displayPath: `${pathStart}Max Retakes`,
			displayType: 'Label',
			legacyName:
				'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.FieldLabel.MaxRetakes',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsLabelNetAcademyId: {
			defaultValue: 'Net Academy ID',
			description: 'Column heading for: Net Academy ID.',
			displayPath: `${pathStart}Net Academy ID`,
			displayType: 'Label',
			legacyName:
				'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.FieldLabel.NetAcademyId',
			...sharedProperties,
		},
		SPCandidateHistoryExamVouchersDetailsErrorMaxAttemptsNumeric: {
			defaultValue: 'Max attempts must be a positive integer',
			description: 'Error saying max attempts must be a positive number.',
			displayPath: `${pathStart}Max Attempts Numeric`,
			displayType: 'Error',
			legacyName:
				'Exam.CertMetricsVouchers/Candidates.ExamVoucherDetails.FieldError.MaxAttemptsNumeric',
			...sharedProperties,
		},
	};

export type SPCandidateHistoryExamVouchersDetailsLocType =
	LocMap<SPCandidateHistoryExamVouchersDetailsLocNames>;
